import { useEffect, useRef, useState, useMemo } from 'react';
import './AboutUsPage.css';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://permataharapansecondary.sch.id/api';
const API_ORIGIN = API_BASE.replace(/\/api\/?$/, '');

const extractYouTubeId = (url) => {
  if (!url || typeof url !== 'string') return null;
  const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/i);
  return match ? match[1] : null;
};

const isVideoUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  const clean = url.split('?')[0].toLowerCase();
  return (
    clean.endsWith('.mp4') ||
    clean.endsWith('.webm') ||
    clean.endsWith('.ogg') ||
    clean.endsWith('.mov') ||
    url.includes('youtube.com') ||
    url.includes('youtu.be') ||
    url.includes('/videos/')
  );
};

const resolveMediaUrl = (mediaId, directUrl, mediaMap = {}) => {
  if (mediaId && mediaMap && (mediaMap[mediaId] || mediaMap[String(mediaId)])) {
    return mediaMap[mediaId] || mediaMap[String(mediaId)];
  }
  const candidate = directUrl || (typeof mediaId === 'string' && isNaN(Number(mediaId)) ? mediaId : null);
  if (candidate && typeof candidate === 'string') {
    if (candidate.startsWith('http://') || candidate.startsWith('https://') || candidate.startsWith('blob:') || candidate.startsWith('data:')) {
      return candidate;
    }
    if (candidate.startsWith('/')) {
      const path = (candidate.startsWith('/api/') || candidate === '/api') ? candidate : `/api${candidate}`;
      return `${API_ORIGIN}${path}`;
    }
    if (candidate.includes('/uploads/')) {
      return `${API_ORIGIN}/api/${candidate.replace(/^\/+/, '')}`;
    }
    return `${API_ORIGIN}/api/uploads/homepage/about-us/${candidate.replace(/^\/+/, '')}`;
  }
  return '';
};

function AboutUsPage({ page }) {
  const [liveSections, setLiveSections] = useState(null);
  const [mediaMap, setMediaMap] = useState(() => {
    try {
      const cached = sessionStorage.getItem('cms_media_homepage');
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      return {};
    }
  });

  // Fetch published sections and media directly from database API
  useEffect(() => {
    let isMounted = true;
    const loadPublishedData = async () => {
      try {
        const [secRes, mediaHomeRes, mediaAboutRes] = await Promise.allSettled([
          fetch(`${API_BASE}/aboutus/cms/sections?published=true&_t=${Date.now()}`, { credentials: 'omit' }),
          fetch(`${API_BASE}/homepage/cms/media?type=image`, { credentials: 'omit' }),
          fetch(`${API_BASE}/aboutus/cms/media`, { credentials: 'omit' })
        ]);

        const map = {};
        for (const res of [mediaHomeRes, mediaAboutRes]) {
          if (res.status === 'fulfilled' && res.value?.ok) {
            try {
              const json = await res.value.json();
              const items = Array.isArray(json.data) ? json.data : [];
              items.forEach(m => {
                const id = m.id || m.media_id;
                const url = m.url || m.file_path;
                if (id && url) {
                  map[id] = url;
                  map[String(id)] = url;
                }
              });
            } catch (_) {}
          }
        }

        if (isMounted && Object.keys(map).length > 0) {
          setMediaMap(prev => ({ ...prev, ...map }));
          try { sessionStorage.setItem('cms_media_homepage', JSON.stringify(map)); } catch (_) {}
        }

        if (secRes.status === 'fulfilled' && secRes.value?.ok) {
          const json = await secRes.value.json();
          const sections = Array.isArray(json.data) ? json.data : Object.values(json.data || {});
          if (sections.length > 0 && isMounted) {
            const sectionMap = {};
            sections.forEach(s => {
              const cp = s.content_published || s.content;
              let parsed = cp;
              if (typeof cp === 'string') {
                try { parsed = JSON.parse(cp); } catch (e) {}
              }
              const contentEn = parsed?.en || parsed || {};
              sectionMap[s.section_type] = contentEn;
            });
            setLiveSections(sectionMap);
          }
        }
      } catch (err) {
        console.warn('[AboutUs] Data load warning:', err);
      }
    };

    loadPublishedData();

    const handleApproval = () => {
      loadPublishedData();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        loadPublishedData();
      }
    };

    window.addEventListener('approvalCompleted', handleApproval);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => { 
      isMounted = false; 
      window.removeEventListener('approvalCompleted', handleApproval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  // Map 8 sections strictly from published content
  const heroSec = liveSections?.['about-us-hero-shell'] || page?.raw_sections?.['about-us-hero-shell'] || {};
  const princSec = liveSections?.['about-us-principal-shell'] || page?.raw_sections?.['about-us-principal-shell'] || page?.raw_sections?.['about-us-principal-message'] || {};
  const valSec = liveSections?.['about-us-values-shell'] || page?.raw_sections?.['about-us-values-shell'] || {};
  const philSec = liveSections?.['about-us-philosophy-shell'] || page?.raw_sections?.['about-us-philosophy-shell'] || {};
  const histSec = liveSections?.['about-us-history-shell'] || page?.raw_sections?.['about-us-history-shell'] || {};
  const whySec = liveSections?.['about-us-why-started-shell'] || page?.raw_sections?.['about-us-why-started-shell'] || {};
  const estSec = liveSections?.['about-us-established-shell'] || page?.raw_sections?.['about-us-established-shell'] || {};
  const leadSec = liveSections?.['about-us-leadership-shell'] || page?.raw_sections?.['about-us-leadership-shell'] || {};

  // Hero Data
  const heroTitle = heroSec.title || page?.hero_title || page?.title || 'About Permata Harapan';
  const heroSummary = heroSec.summary || page?.hero_summary || page?.summary || '';

  const rawSlides = useMemo(() => {
    if (Array.isArray(heroSec.slides) && heroSec.slides.length > 0) return heroSec.slides;
    if (Array.isArray(page?.hero_slides) && page.hero_slides.length > 0) return page.hero_slides;
    if (Array.isArray(page?.slides) && page.slides.length > 0) return page.slides;
    return [];
  }, [heroSec.slides, page?.hero_slides, page?.slides]);

  const heroSlides = useMemo(() => {
    const list = rawSlides.map((slide, idx) => {
      const rawUrl = typeof slide === 'object' && slide !== null
        ? (slide.image || slide.url || slide.video || '')
        : (typeof slide === 'string' ? slide : '');
      const resolved = resolveMediaUrl(rawUrl, rawUrl, mediaMap);
      const videoCandidate = (typeof slide === 'object' && slide !== null && slide.video)
        ? resolveMediaUrl(slide.video, slide.video, mediaMap)
        : (isVideoUrl(resolved) ? resolved : null);
      const isVideo = Boolean(videoCandidate);
      const youtubeId = isVideo ? extractYouTubeId(videoCandidate) : null;
      return {
        id: (typeof slide === 'object' && slide?.order) ? slide.order : idx,
        url: videoCandidate || resolved,
        isVideo,
        isYouTube: Boolean(youtubeId),
        youtubeId
      };
    }).filter(s => Boolean(s.url));

    // Check single hero video if slides has none
    const singleVideo = heroSec.video || heroSec.video_url || heroSec.video_file || page?.hero_video;
    if (list.length === 0 && singleVideo) {
      const resolvedVid = resolveMediaUrl(singleVideo, singleVideo, mediaMap);
      const ytId = extractYouTubeId(resolvedVid);
      list.push({
        id: 'hero-vid-0',
        url: resolvedVid,
        isVideo: true,
        isYouTube: Boolean(ytId),
        youtubeId: ytId
      });
    }

    return list;
  }, [rawSlides, mediaMap, heroSec.video, heroSec.video_url, heroSec.video_file, page?.hero_video]);

  const [activeHero, setActiveHero] = useState(0);
  const [selectedValue, setSelectedValue] = useState(null);
  const [animateWelcome, setAnimateWelcome] = useState(false);

  // Hero auto-rotation
  useEffect(() => {
    if (heroSlides.length <= 1) return;
    const intervalId = setInterval(() => {
      setActiveHero(curr => (curr + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  const goToPrevHero = () => {
    if (heroSlides.length <= 1) return;
    setActiveHero(curr => (curr - 1 + heroSlides.length) % heroSlides.length);
  };

  const goToNextHero = () => {
    if (heroSlides.length <= 1) return;
    setActiveHero(curr => (curr + 1) % heroSlides.length);
  };

  // Welcome message intersection observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimateWelcome(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    const welcomeEl = document.getElementById('welcome-message');
    if (welcomeEl) observer.observe(welcomeEl);

    return () => observer.disconnect();
  }, []);

  // History timeline data
  const rawTimeline = useMemo(() => {
    if (Array.isArray(histSec.items) && histSec.items.length > 0) return histSec.items;
    if (Array.isArray(page?.history_timeline) && page.history_timeline.length > 0) return page.history_timeline;
    if (Array.isArray(page?.history) && page.history.length > 0) return page.history;
    return [];
  }, [histSec.items, page?.history_timeline, page?.history]);

  const historyTimeline = useMemo(() => {
    return rawTimeline.map((item, idx) => ({
      ...item,
      year: item.year || '',
      title: item.title || '',
      description: item.description || '',
      image: resolveMediaUrl(item.image_id || item.image, item.image_url || item.image, mediaMap)
    }));
  }, [rawTimeline, mediaMap]);

  const [activeHistoryIndex, setActiveHistoryIndex] = useState(0);
  const [expandedHistoryImage, setExpandedHistoryImage] = useState(null);

  // Why started & established data
  const whyStarted = {
    headline: whySec.headline || page?.why_started?.headline || page?.why_started?.why_started_headline || page?.why_started_headline || '',
    paragraph: whySec.paragraph || page?.why_started?.paragraph || page?.why_started?.why_started_paragraph || page?.why_started_paragraph || '',
    tags: Array.isArray(whySec.tags) ? whySec.tags : (Array.isArray(page?.why_started?.tags) ? page.why_started.tags : (Array.isArray(page?.why_started_tags) ? page.why_started_tags : [])),
    note: whySec.note || page?.why_started?.note || page?.why_started?.why_started_note || page?.why_started_note || '',
  };

  const established = {
    date: estSec.date || page?.established?.date || page?.established?.established_date || page?.established_date || '',
    facts: Array.isArray(estSec.facts) ? estSec.facts : (Array.isArray(page?.established?.facts) ? page.established.facts : (Array.isArray(page?.established_facts) ? page.established_facts : [])),
    cta: estSec.cta || page?.established?.cta || page?.established?.established_cta || page?.established_cta || '',
  };

  // Principal Profile
  const profile = {
    name: princSec.name || page?.principal_name || '',
    credentials: princSec.credentials || page?.principal_credentials || '',
    role: princSec.role || page?.principal_role || 'Principal / Head of School',
    opening: princSec.opening || page?.principal_opening || '',
    paragraphOne: princSec.paragraph_one || page?.principal_paragraph_one || '',
    paragraphTwo: princSec.paragraph_two || page?.principal_paragraph_two || '',
    closing: princSec.closing || page?.principal_closing || '',
    themeLine: princSec.theme_line || page?.principal_theme_line || '',
    photo: resolveMediaUrl(
      princSec.principal_photo_id || princSec.photo || page?.principal_photo_id || page?.principal_photo,
      princSec.principal_photo_url || page?.principal_photo_url,
      mediaMap
    ),
  };

  // Leadership profiles
  const rawLeaders = useMemo(() => {
    if (Array.isArray(leadSec.items) && leadSec.items.length > 0) return leadSec.items;
    if (Array.isArray(page?.leadership_profiles) && page.leadership_profiles.length > 0) return page.leadership_profiles;
    if (Array.isArray(page?.leadership) && page.leadership.length > 0) return page.leadership;
    return [];
  }, [leadSec.items, page?.leadership_profiles, page?.leadership]);

  const leadershipProfiles = useMemo(() => {
    return rawLeaders.map(leader => ({
      ...leader,
      name: leader.name || '',
      role: leader.role || '',
      bio: leader.bio || '',
      photo: resolveMediaUrl(leader.photo_id || leader.photo, leader.photo_url || leader.photo, mediaMap)
    }));
  }, [rawLeaders, mediaMap]);

  // Philosophy data
  const rawPhilosophy = useMemo(() => {
    if (Array.isArray(philSec.items) && philSec.items.length > 0) return philSec.items;
    if (Array.isArray(page?.school_philosophy) && page.school_philosophy.length > 0) return page.school_philosophy;
    if (Array.isArray(page?.philosophy) && page.philosophy.length > 0) return page.philosophy;
    return [];
  }, [philSec.items, page?.school_philosophy, page?.philosophy]);

  const philosophyItems = useMemo(() => {
    return rawPhilosophy.map((item, idx) => ({
      ...item,
      key: item.key || `philosophy-${idx}`,
      label: item.label || item.title || `Philosophy ${idx + 1}`,
      shortTitle: item.shortTitle || item.title || '',
      title: item.title || '',
      description: item.description || '',
      points: Array.isArray(item.points) ? item.points : [],
      quote: item.quote || '',
      index: item.index || `0${idx + 1}`,
      image: resolveMediaUrl(item.image_id || item.image, item.image_url || item.image, mediaMap)
    }));
  }, [rawPhilosophy, mediaMap]);

  const [activePhilosophy, setActivePhilosophy] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(false);
  const [expandedPhilosophyImage, setExpandedPhilosophyImage] = useState(null);
  const [selectedLeader, setSelectedLeader] = useState(null);
  const touchStartXRef = useRef(null);

  useEffect(() => {
    if (!expandedPhilosophyImage && !expandedHistoryImage && !selectedLeader) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setExpandedPhilosophyImage(null);
        setExpandedHistoryImage(null);
        setSelectedLeader(null);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [expandedPhilosophyImage, expandedHistoryImage, selectedLeader]);

  const goToPreviousPhilosophy = () => {
    if (philosophyItems.length <= 1) return;
    setActivePhilosophy(curr => (curr - 1 + philosophyItems.length) % philosophyItems.length);
  };

  const goToNextPhilosophy = () => {
    if (philosophyItems.length <= 1) return;
    setActivePhilosophy(curr => (curr + 1) % philosophyItems.length);
  };

  const handlePhilosophyTouchStart = (e) => {
    touchStartXRef.current = e.touches?.[0]?.clientX ?? null;
  };

  const handlePhilosophyTouchEnd = (e) => {
    if (touchStartXRef.current === null) return;
    const endX = e.changedTouches?.[0]?.clientX ?? touchStartXRef.current;
    const deltaX = endX - touchStartXRef.current;
    if (Math.abs(deltaX) >= 50) {
      if (deltaX > 0) goToPreviousPhilosophy();
      else goToNextPhilosophy();
    }
    touchStartXRef.current = null;
  };

  // Values Cards data with complete fallback resolution for Vision, Mission & Core Values
  const rawCards = useMemo(() => {
    if (Array.isArray(valSec.cards) && valSec.cards.length > 0) return valSec.cards;
    if (Array.isArray(page?.cards) && page.cards.length > 0) return page.cards;
    if (Array.isArray(page?.values_cards) && page.values_cards.length > 0) return page.values_cards;

    return [
      {
        key: 'vision',
        label: valSec.vision_label || page?.vision_label || 'Vision',
        title: valSec.vision_title || page?.vision_title || 'To be inspired by faith, to be strengthened with fortitude, and to be motivated to achieve fulfillment through lifelong learning',
        description: valSec.vision_description || page?.vision_description || 'Our vision guides every decision we make, ensuring that faith, courage, and purpose are at the heart of our educational journey.',
        image: valSec.vision_image || page?.vision_image || ''
      },
      {
        key: 'mission',
        label: valSec.mission_label || page?.mission_label || 'Mission',
        title: valSec.mission_title || page?.mission_title || 'PHB Secondary School is dedicated to empower students through a values-driven education, nurturing each student\'s spiritual, intellectual, and personal growth, inspiring them to face challenges with resilience, purpose, and a commitment to excellence.',
        description: valSec.mission_description || page?.mission_description || 'We empower every student to become confident learners, compassionate leaders, and responsible global citizens through rigorous academics and character formation.',
        image: valSec.mission_image || page?.mission_image || ''
      },
      {
        key: 'core_values',
        label: valSec.core_values_label || page?.core_values_label || 'Core Values',
        title: valSec.core_values_title || page?.core_values_title || 'Sekolah Permata Harapan Secondary is dedicated to empower students through a values-driven education, nurturing each student\'s spiritual, intellectual, and personal growth, inspiring them to face challenges with resilience, purpose, and a commitment to excellence.',
        description: valSec.core_values_description || page?.core_values_description || 'Our core values of integrity, excellence, compassion, and service define who we are and guide how we treat one another every day.',
        image: valSec.core_values_image || page?.core_values_image || ''
      }
    ];
  }, [valSec, page]);

  const valuesSection = {
    kicker: valSec.kicker || page?.values_kicker || page?.kicker || 'Our Direction',
    title: valSec.title || page?.values_title || page?.title || 'Vision, Mission, and Core Values',
    intro: valSec.intro || page?.values_intro || page?.intro || '',
    cards: rawCards.map((card, idx) => {
      const isVision = card.key === 'vision' || idx === 0;
      const isMission = card.key === 'mission' || idx === 1;

      const fallbackLabel = isVision ? 'Vision' : (isMission ? 'Mission' : 'Core Values');
      const fallbackTitle = isVision
        ? (valSec.vision_title || page?.vision_title || '')
        : (isMission
            ? (valSec.mission_title || page?.mission_title || '')
            : (valSec.core_values_title || page?.core_values_title || ''));
      const fallbackDesc = isVision
        ? (valSec.vision_description || page?.vision_description || '')
        : (isMission
            ? (valSec.mission_description || page?.mission_description || '')
            : (valSec.core_values_description || page?.core_values_description || ''));

      return {
        ...card,
        key: card.key || `card-${idx}`,
        label: card.label || fallbackLabel,
        title: card.title || fallbackTitle || card.label || fallbackLabel,
        description: card.description || card.detailed_statement || card.statement || card.detail || fallbackDesc,
        image: resolveMediaUrl(card.image_id || card.image, card.image_url || card.image, mediaMap)
      };
    })
  };

  return (
    <section className="about-us-page">
      {/* Redesigned Hero Section */}
      <section className="about-hero" id="about-overview">
        <div className="about-hero-media" aria-hidden="true">
          {heroSlides.length > 0 ? (
            heroSlides.map((slide, index) => {
              const isCurrent = index === activeHero;
              return (
                <div
                  key={slide.id || index}
                  className={`about-hero-slide ${isCurrent ? 'active' : ''}`}
                >
                  {slide.isVideo ? (
                    slide.isYouTube ? (
                      <iframe
                        className="about-hero-video-frame"
                        src={`https://www.youtube.com/embed/${slide.youtubeId}?autoplay=1&mute=1&loop=1&playlist=${slide.youtubeId}&controls=0&playsinline=1&modestbranding=1&rel=0`}
                        title="Hero video background"
                        allow="autoplay; encrypted-media"
                        tabIndex="-1"
                      />
                    ) : (
                      <video
                        className="about-hero-video-element"
                        src={slide.url}
                        autoPlay
                        loop
                        muted
                        playsInline
                        preload="auto"
                      />
                    )
                  ) : (
                    <img
                      src={slide.url}
                      alt={heroTitle}
                      className="about-hero-img-element"
                    />
                  )}
                </div>
              );
            })
          ) : (
            <div className="about-hero-slide active about-hero-gradient-fallback" />
          )}
        </div>

        <div className="about-hero-overlay" aria-hidden="true" />

        <div className="about-hero-content-wrap">
          <div className="about-hero-content">
            <p className="about-hero-kicker">
              About Permata Harapan
            </p>

            <h1 className="about-hero-title">
              {heroTitle}
            </h1>

            {heroSummary && (
              <p className="about-hero-summary">
                {heroSummary}
              </p>
            )}

            {heroSlides.length > 1 && (
              <div className="about-hero-dots" role="tablist" aria-label="Hero carousel navigation">
                {heroSlides.map((slide, index) => (
                  <button
                    key={slide.id || index}
                    type="button"
                    className={`about-hero-dot ${index === activeHero ? 'active' : ''}`}
                    onClick={() => setActiveHero(index)}
                    aria-label={`Slide ${index + 1}`}
                    role="tab"
                    aria-selected={index === activeHero}
                  >
                    <span className="about-hero-dot-bar" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Welcome / Principal Message Section - 1 Unified Master Card */}
      <section className="about-welcome-section" id="welcome-message">
        <div className="about-welcome-container">
          <div className={`about-welcome-master-card about-animate ${animateWelcome ? 'animate-in' : ''}`}>
            {/* Top Multi-Tone Prestige Ribbon */}
            <div className="about-master-card-ribbon" />

            <div className="about-master-card-grid">
              {/* Left Column: Portrait & Plaque */}
              <aside className="about-master-photo-col" aria-label="Head of School">
                <div className="about-master-photo-frame">
                  {profile.photo ? (
                    <img src={profile.photo} alt={profile.name || 'Head of School'} className="about-master-photo" />
                  ) : (
                    <div className="about-head-photo-placeholder">
                      <span>{profile.name ? profile.name.charAt(0) : 'PH'}</span>
                    </div>
                  )}
                  <div className="about-master-photo-shine" />
                </div>
                <div className="about-master-photo-caption">
                  <div className="about-photo-caption-emblem" aria-hidden="true">✦</div>
                  {profile.role && <p className="about-side-role">{profile.role}</p>}
                  {profile.name && <h3 className="about-side-quote">{profile.name}</h3>}
                  {profile.themeLine && (
                    <p className="about-side-theme">
                      &ldquo;{profile.themeLine.replace(/^["“”']+|["“”']+$/g, '')}&rdquo;
                    </p>
                  )}
                </div>
              </aside>

              {/* Right Column: Letterhead Message */}
              <article className="about-master-message-col">
                <div className="about-master-card-watermark" aria-hidden="true">“</div>

                <div className="about-master-message-inner">
                  <div className="about-message-kicker-wrap">
                    <span className="about-message-kicker-badge">
                      <span className="about-message-kicker-dot" />
                      <span>{princSec.kicker || page?.welcome_kicker || 'Welcome Message'}</span>
                    </span>
                  </div>

                  <h2 className="about-message-title">{princSec.title || page?.welcome_title || 'From the Principal / Head of School'}</h2>
                  {(princSec.subtitle || page?.welcome_subtitle) && (
                    <p className="about-message-subtitle">{princSec.subtitle || page?.welcome_subtitle}</p>
                  )}

                  {profile.opening && <p className="about-message-intro">{profile.opening}</p>}
                  {profile.paragraphOne && <p className="about-message-body-p">{profile.paragraphOne}</p>}
                  {profile.paragraphTwo && <p className="about-message-body-p">{profile.paragraphTwo}</p>}
                  {profile.closing && <p className="about-message-closing-p">{profile.closing}</p>}

                  {(profile.name || profile.role) && (
                    <div className="about-message-sign">
                      <div className="about-message-sign-info">
                        {profile.name && (
                          <div className="about-message-signature-line" aria-hidden="true">
                            {profile.name}
                          </div>
                        )}
                        <strong>{profile.name}{profile.credentials ? `, ${profile.credentials}` : ''}</strong>
                        <span>{profile.role} • Permata Harapan School</span>
                      </div>

                      <div className="about-message-seal" title="Official Seal of Permata Harapan School">
                        <div className="about-seal-inner">
                          <span className="about-seal-star">★ ★ ★</span>
                          <span className="about-seal-abbr">PHS</span>
                          <span className="about-seal-label">OFFICIAL SEAL</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </article>
            </div>
          </div>
        </div>
      </section>

      {/* Vision, Mission, Values Section - Interactive Reveal on Click */}
      {valuesSection.cards.length > 0 && (
        <section className="about-us-values-shell" id="vision-mission-values">
          <div className="about-us-values-container">
            <div className="about-us-values-header">
              <div className="about-values-kicker-wrap">
                <span className="about-values-kicker">{valuesSection.kicker}</span>
              </div>
              <h3>{valuesSection.title}</h3>
              {valuesSection.intro && <p>{valuesSection.intro}</p>}
            </div>

            <div className="about-values-grid">
              {valuesSection.cards.map((card, idx) => {
                const isSelected = selectedValue?.key === card.key;
                return (
                  <div
                    className={`value-card-new ${isSelected ? 'expanded' : ''}`}
                    key={card.key}
                    onClick={() => setSelectedValue(isSelected ? null : card)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedValue(isSelected ? null : card);
                      }
                    }}
                    aria-expanded={isSelected}
                  >
                    {!isSelected ? (
                      <div className="value-card-front">
                        <div className="value-card-new-image">
                          {card.image ? (
                            <img src={card.image} alt={card.label} />
                          ) : (
                            <div className="value-card-placeholder" />
                          )}
                          <div className="value-card-badge">
                            <span>{String(idx + 1).padStart(2, '0')}</span>
                            <span>•</span>
                            <span>{card.label}</span>
                          </div>
                        </div>

                        <div className="value-card-new-content">
                          <h3>{card.label}</h3>
                          <span className="value-card-click-hint">Click to View Statement</span>
                        </div>

                        <div className="value-card-bottom-accent" />
                      </div>
                    ) : (
                      <div className="value-card-expanded-content">
                        <div className="value-card-expanded-badge">
                          <span>{String(idx + 1).padStart(2, '0')}</span>
                          <span>•</span>
                          <span>{card.label}</span>
                        </div>

                        {card.title && card.title !== card.label ? (
                          <div className="value-card-statement-wrap">
                            <h4 className="value-card-statement-main">
                              &ldquo;{card.title}&rdquo;
                            </h4>
                            {card.description && (
                              <p className="value-card-statement-detail">
                                {card.description}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="value-card-statement-wrap">
                            <h4 className="value-card-statement-title">{card.label}</h4>
                            {card.description && (
                              <p className="value-card-statement-detail">
                                {card.description}
                              </p>
                            )}
                          </div>
                        )}

                        <div className="value-card-close-hint">
                          <span>Click to Close</span>
                        </div>

                        <div className="value-card-bottom-accent" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* School Philosophy Section - Redesigned Grand Showcase */}
      {philosophyItems.length > 0 && (
        <section className="about-philosophy-section" id="school-philosophy">
          <div className="about-philosophy-container">
            {/* Centered Section Header */}
            <div className="about-philosophy-header">
              <div className="about-philosophy-kicker-wrap">
                <span className="about-philosophy-kicker-badge">
                  <span className="about-philosophy-kicker-dot" />
                  <span>{philSec.kicker || 'School Philosophy'}</span>
                </span>
              </div>
              <h2 className="about-philosophy-title">
                {philSec.title || 'Our Guiding Principles'}
              </h2>
              <div className="about-philosophy-title-accent" aria-hidden="true" />
              <p className="about-philosophy-subtitle">
                {philSec.subtitle || philSec.intro || 'Discover the core values, philosophy, and symbolic identity that guide Permata Harapan School.'}
              </p>
            </div>

            {/* Centered Segmented Control / Pill Bar */}
            <div className="about-philosophy-tabs-bar" role="tablist" aria-label="School philosophy tabs">
              {philosophyItems.map((item, index) => (
                <button
                  key={item.key}
                  id={`philosophy-tab-${item.key}`}
                  type="button"
                  className={`about-philosophy-pill-tab ${index === activePhilosophy ? 'active' : ''}`}
                  onClick={() => {
                    setActivePhilosophy(index);
                    setIsAutoPlay(false);
                  }}
                  role="tab"
                  aria-selected={index === activePhilosophy}
                  aria-controls="philosophy-panel"
                >
                  <span className="about-philosophy-pill-index">{item.index}</span>
                  <span className="about-philosophy-pill-title">{item.label || item.shortTitle}</span>
                  {index === activePhilosophy && <span className="about-philosophy-pill-indicator" />}
                </button>
              ))}
            </div>

            {/* Main Grand Showcase Card */}
            <div className="about-philosophy-stage-wrapper">
              {philosophyItems[activePhilosophy] && (
                <article
                  className="about-philosophy-stage-card"
                  id="philosophy-panel"
                  role="tabpanel"
                  aria-labelledby={`philosophy-tab-${philosophyItems[activePhilosophy].key}`}
                  key={philosophyItems[activePhilosophy].key}
                  onTouchStart={handlePhilosophyTouchStart}
                  onTouchEnd={handlePhilosophyTouchEnd}
                >
                  {/* Top Multi-Tone Prestige Ribbon */}
                  <div className="about-philosophy-stage-ribbon" />

                  {/* Watermark Quote */}
                  <div className="about-philosophy-stage-watermark" aria-hidden="true">“</div>

                  <div className="about-philosophy-stage-grid">
                    {/* Visual Showcase Column (1:1 Aspect Ratio) */}
                    <div className="about-philosophy-visual-col">
                      <div className="about-philosophy-media-box">
                        {philosophyItems[activePhilosophy].image ? (
                          <div
                            className="about-philosophy-image-frame has-image"
                            onClick={() => setExpandedPhilosophyImage(philosophyItems[activePhilosophy])}
                            title="Click to view full image"
                            role="button"
                            tabIndex="0"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                setExpandedPhilosophyImage(philosophyItems[activePhilosophy]);
                              }
                            }}
                          >
                            <div className="about-philosophy-zoom-tag" aria-hidden="true">
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8" />
                                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                                <line x1="11" y1="8" x2="11" y2="14" />
                                <line x1="8" y1="11" x2="14" y2="11" />
                              </svg>
                              <span>1:1 Diagram • Tap to Enlarge</span>
                            </div>

                            <img
                              src={philosophyItems[activePhilosophy].image}
                              alt={philosophyItems[activePhilosophy].label || 'School Philosophy Diagram'}
                              className="about-philosophy-stage-img"
                              loading="lazy"
                            />

                            <div className="about-philosophy-caption-dock">
                              <span className="about-philosophy-caption-dot" />
                              <span>{philosophyItems[activePhilosophy].label || 'PHS Diagram'}</span>
                            </div>
                          </div>
                        ) : (
                          <div className="about-philosophy-image-frame is-crest" aria-hidden="true">
                            <div className="about-philosophy-crest-inner">
                              <div className="about-philosophy-crest-disc">
                                <span className="about-philosophy-crest-monogram">PHS</span>
                              </div>
                              <p className="about-philosophy-crest-motto-text">
                                &ldquo;{philosophyItems[activePhilosophy].shortTitle || 'Non Scholae Sed Vitae Discimus'}&rdquo;
                              </p>
                              <span className="about-philosophy-crest-tag">Sekolah Permata Harapan</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Editorial Content Column */}
                    <div className="about-philosophy-editorial-col">
                      <div className="about-philosophy-editorial-header">
                        <div className="about-philosophy-badge-row">
                          <span className="about-philosophy-kicker-tag">
                            <span className="about-philosophy-tag-bullet" />
                            <span>{philosophyItems[activePhilosophy].label}</span>
                          </span>
                          <span className="about-philosophy-order-mark">
                            {philosophyItems[activePhilosophy].index}
                          </span>
                        </div>

                        <h3 className="about-philosophy-heading">
                          {philosophyItems[activePhilosophy].title}
                        </h3>

                        {philosophyItems[activePhilosophy].description && (
                          <p className="about-philosophy-lead">
                            {philosophyItems[activePhilosophy].description}
                          </p>
                        )}
                      </div>

                      {/* Pillars / Key Points */}
                      {philosophyItems[activePhilosophy].points?.length > 0 && (
                        <div className="about-philosophy-cards-stack">
                          {philosophyItems[activePhilosophy].points.map((point, idx) => (
                            <div
                              className="about-philosophy-card-item"
                              key={point.title || idx}
                              style={{ animationDelay: `${idx * 70}ms` }}
                            >
                              <div className="about-philosophy-card-accent" />
                              <div className="about-philosophy-card-content">
                                <div className="about-philosophy-card-head">
                                  <span className="about-philosophy-card-bullet" />
                                  <h4 className="about-philosophy-card-title">{point.title}</h4>
                                </div>
                                <p className="about-philosophy-card-text">{point.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Callout Quote */}
                      {philosophyItems[activePhilosophy].quote && (
                        <div className="about-philosophy-quote-box">
                          <div className="about-philosophy-quote-mark" aria-hidden="true">&ldquo;</div>
                          <p className="about-philosophy-quote-content">
                            {philosophyItems[activePhilosophy].quote.replace(/^["“”']+|["“”']+$/g, '')}
                          </p>
                        </div>
                      )}

                      {/* Integrated Stage Navigation Bar */}
                      {philosophyItems.length > 1 && (
                        <div className="about-philosophy-stage-footer">
                          <div className="about-philosophy-stepper-dots" role="tablist" aria-label="Philosophy tabs">
                            {philosophyItems.map((_, dotIdx) => (
                              <button
                                key={dotIdx}
                                type="button"
                                className={`about-philosophy-stepper-dot ${dotIdx === activePhilosophy ? 'active' : ''}`}
                                onClick={() => {
                                  setActivePhilosophy(dotIdx);
                                  setIsAutoPlay(false);
                                }}
                                aria-label={`Go to slide ${dotIdx + 1}`}
                              />
                            ))}
                          </div>

                          <div className="about-philosophy-nav-btns">
                            <button
                              type="button"
                              className="about-philosophy-nav-btn prev"
                              onClick={goToPreviousPhilosophy}
                              aria-label="Previous philosophy"
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="15 18 9 12 15 6" />
                              </svg>
                            </button>
                            <span className="about-philosophy-nav-count">
                              {activePhilosophy + 1} / {philosophyItems.length}
                            </span>
                            <button
                              type="button"
                              className="about-philosophy-nav-btn next"
                              onClick={goToNextPhilosophy}
                              aria-label="Next philosophy"
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="9 18 15 12 9 6" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              )}
            </div>

            {/* High-Resolution Diagram Lightbox Modal */}
            {expandedPhilosophyImage && (
              <div
                className="about-philosophy-modal-backdrop"
                onClick={() => setExpandedPhilosophyImage(null)}
                role="dialog"
                aria-modal="true"
                aria-label="Diagram Preview"
              >
                <div className="about-philosophy-modal-card" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    className="about-philosophy-modal-close"
                    onClick={() => setExpandedPhilosophyImage(null)}
                    aria-label="Close modal"
                  >
                    &times;
                  </button>
                  <div className="about-philosophy-modal-header">
                    <span className="about-philosophy-modal-badge">
                      {expandedPhilosophyImage.label || 'Philosophy Diagram'}
                    </span>
                    <h4>{expandedPhilosophyImage.title || ''}</h4>
                  </div>
                  <div className="about-philosophy-modal-image-wrap">
                    <img
                      src={expandedPhilosophyImage.image}
                      alt={expandedPhilosophyImage.label || 'Full size diagram'}
                      className="about-philosophy-modal-img"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {/* History & Heritage Section - Redesigned Royal Purple Showcase */}
      <section className="about-history-shell" id="our-history">
        <div className="about-history-container">
          {/* Centered Section Header */}
          <div className="about-history-header">
            <div className="about-history-kicker-wrap">
              <span className="about-history-kicker-badge">
                <span className="about-history-kicker-dot" />
                <span>{histSec.kicker || 'Our Journey'}</span>
              </span>
            </div>
            <h2 className="about-history-title">
              {histSec.title || 'Our Beginning & Heritage'}
            </h2>
            <div className="about-history-title-accent" aria-hidden="true" />
            <p className="about-history-subtitle">
              {histSec.subtitle || histSec.intro || 'A brief story of how and why Permata Harapan School was founded.'}
            </p>
          </div>

          {/* Interactive Milestone Timeline */}
          {historyTimeline.length > 0 && (
            <div className="about-history-timeline-showcase">
              {/* Horizontal Milestone Roadmap Track */}
              <div className="about-history-roadmap-wrap" role="tablist" aria-label="School history milestones">
                <div className="about-history-roadmap-line" aria-hidden="true" />
                <div className="about-history-roadmap-nodes">
                  {historyTimeline.map((item, index) => {
                    const isActive = index === activeHistoryIndex;
                    return (
                      <button
                        key={item.year || index}
                        id={`history-tab-${index}`}
                        type="button"
                        className={`about-history-roadmap-node ${isActive ? 'active' : ''}`}
                        onClick={() => setActiveHistoryIndex(index)}
                        role="tab"
                        aria-selected={isActive}
                        aria-controls="history-stage-panel"
                      >
                        <span className="about-history-node-marker">
                          <span className="about-history-node-dot" />
                        </span>
                        <span className="about-history-node-year">{item.year}</span>
                        {item.title && (
                          <span className="about-history-node-title">{item.title}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Milestone Spotlight Stage Card */}
              {historyTimeline[activeHistoryIndex] && (
                <article
                  className="about-history-stage-card"
                  id="history-stage-panel"
                  role="tabpanel"
                  aria-labelledby={`history-tab-${activeHistoryIndex}`}
                  key={`milestone-${activeHistoryIndex}-${historyTimeline[activeHistoryIndex].year}`}
                >
                  <div className="about-history-stage-ribbon" />
                  <div className="about-history-stage-watermark" aria-hidden="true">
                    {historyTimeline[activeHistoryIndex].year}
                  </div>

                  <div className="about-history-stage-grid">
                    {/* Visual Media Column */}
                    <div className="about-history-visual-col">
                      {historyTimeline[activeHistoryIndex].image ? (
                        <div
                          className="about-history-image-frame"
                          onClick={() => setExpandedHistoryImage(historyTimeline[activeHistoryIndex])}
                          title="Click to view full image"
                        >
                          <img
                            src={historyTimeline[activeHistoryIndex].image}
                            alt={historyTimeline[activeHistoryIndex].title || `Milestone ${historyTimeline[activeHistoryIndex].year}`}
                            className="about-history-stage-img"
                          />
                          <div className="about-history-zoom-badge" aria-hidden="true">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="11" cy="11" r="8" />
                              <line x1="21" y1="21" x2="16.65" y2="16.65" />
                              <line x1="11" y1="8" x2="11" y2="14" />
                              <line x1="8" y1="11" x2="14" y2="11" />
                            </svg>
                            <span>Inspect</span>
                          </div>
                          <div className="about-history-image-caption">
                            <span>{historyTimeline[activeHistoryIndex].year}</span>
                            <strong>{historyTimeline[activeHistoryIndex].title}</strong>
                          </div>
                        </div>
                      ) : (
                        <div className="about-history-crest-frame" aria-hidden="true">
                          <div className="about-history-crest-glow" />
                          <div className="about-history-crest-circle">
                            <span className="about-history-crest-pre">EST.</span>
                            <span className="about-history-crest-year">{historyTimeline[activeHistoryIndex].year}</span>
                            <span className="about-history-crest-post">MILESTONE</span>
                          </div>
                          <p className="about-history-crest-tagline">Permata Harapan School</p>
                        </div>
                      )}
                    </div>

                    {/* Narrative Column */}
                    <div className="about-history-narrative-col">
                      <div className="about-history-meta-bar">
                        <span className="about-history-milestone-pill">
                          Milestone {String(activeHistoryIndex + 1).padStart(2, '0')} / {String(historyTimeline.length).padStart(2, '0')}
                        </span>
                        <span className="about-history-year-tag">{historyTimeline[activeHistoryIndex].year}</span>
                      </div>

                      <h3 className="about-history-card-title">
                        {historyTimeline[activeHistoryIndex].title}
                      </h3>

                      <p className="about-history-card-desc">
                        {historyTimeline[activeHistoryIndex].description}
                      </p>

                      {/* Stepper Navigation */}
                      <div className="about-history-stepper-footer">
                        <div className="about-history-nav-dots" role="tablist" aria-label="Milestone selector">
                          {historyTimeline.map((_, dotIdx) => (
                            <button
                              key={dotIdx}
                              type="button"
                              className={`about-history-nav-dot ${dotIdx === activeHistoryIndex ? 'active' : ''}`}
                              onClick={() => setActiveHistoryIndex(dotIdx)}
                              aria-label={`Go to milestone ${dotIdx + 1}`}
                            />
                          ))}
                        </div>

                        <div className="about-history-nav-buttons">
                          <button
                            type="button"
                            className="about-history-nav-btn prev"
                            onClick={() => setActiveHistoryIndex(curr => (curr - 1 + historyTimeline.length) % historyTimeline.length)}
                            aria-label="Previous milestone"
                          >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="15 18 9 12 15 6" />
                            </svg>
                            <span>Prev</span>
                          </button>
                          <span className="about-history-nav-counter">
                            {activeHistoryIndex + 1} / {historyTimeline.length}
                          </span>
                          <button
                            type="button"
                            className="about-history-nav-btn next"
                            onClick={() => setActiveHistoryIndex(curr => (curr + 1) % historyTimeline.length)}
                            aria-label="Next milestone"
                          >
                            <span>Next</span>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="9 18 15 12 9 6" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </article>
              )}
            </div>
          )}

          {/* Complementary Foundation Dual Cards */}
          {(whyStarted.headline || whyStarted.paragraph || established.date || established.facts.length > 0) && (
            <div className="about-history-foundation-grid">
              {/* Card 1: Why We Started */}
              {(whyStarted.headline || whyStarted.paragraph) && (
                <article className="about-history-foundation-card why-started-card">
                  <div className="about-history-card-kicker-row">
                    <span className="about-history-foundation-badge">
                      <span className="about-history-badge-sparkle" />
                      <span>Why We Started</span>
                    </span>
                  </div>
                  {whyStarted.headline && (
                    <h4 className="about-history-foundation-heading">
                      {whyStarted.headline}
                    </h4>
                  )}
                  {whyStarted.paragraph && (
                    <p className="about-history-foundation-text">
                      {whyStarted.paragraph}
                    </p>
                  )}
                  {whyStarted.tags.length > 0 && (
                    <div className="about-history-tag-cluster">
                      {whyStarted.tags.map((tag, i) => (
                        <span key={i} className="about-history-pill-tag">
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}
                  {whyStarted.note && (
                    <div className="about-history-foundation-note">
                      <span className="about-history-note-bar" />
                      <p>{whyStarted.note}</p>
                    </div>
                  )}
                </article>
              )}

              {/* Card 2: Established & Heritage */}
              {(established.date || established.facts.length > 0 || established.cta) && (
                <article className="about-history-foundation-card established-card">
                  <div className="about-history-card-kicker-row">
                    <span className="about-history-foundation-badge">
                      <span className="about-history-badge-sparkle" />
                      <span>Established</span>
                    </span>
                    <span className="about-history-heritage-since">Heritage & Roots</span>
                  </div>
                  {established.date && (
                    <div className="about-history-established-highlight">
                      <span className="about-history-est-prefix">Rooted in Excellence Since</span>
                      <h4 className="about-history-est-date">{established.date}</h4>
                    </div>
                  )}
                  {established.facts.length > 0 && (
                    <div className="about-history-facts-grid">
                      {established.facts.map((fact, i) => (
                        <div key={i} className="about-history-fact-cell">
                          <span className="about-history-fact-label">{fact.label}</span>
                          <strong className="about-history-fact-value">{fact.value}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                  {established.cta && (
                    <div className="about-history-est-cta">
                      <span className="about-history-cta-icon">&bull;</span>
                      <p className="about-history-cta-text">{established.cta}</p>
                    </div>
                  )}
                </article>
              )}
            </div>
          )}
        </div>

        {/* Fullscreen Image Lightbox Modal for History */}
        {expandedHistoryImage && (
          <div
            className="about-history-modal-backdrop"
            onClick={() => setExpandedHistoryImage(null)}
            role="dialog"
            aria-modal="true"
            aria-label="History Photo Preview"
          >
            <div className="about-history-modal-card" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="about-history-modal-close"
                onClick={() => setExpandedHistoryImage(null)}
                aria-label="Close modal"
              >
                &times;
              </button>
              <div className="about-history-modal-header">
                <span className="about-history-modal-badge">{expandedHistoryImage.year}</span>
                <h4>{expandedHistoryImage.title || 'Historical Milestone'}</h4>
              </div>
              <div className="about-history-modal-image-wrap">
                <img
                  src={expandedHistoryImage.image}
                  alt={expandedHistoryImage.title || 'Historical photo'}
                  className="about-history-modal-img"
                />
              </div>
              {expandedHistoryImage.description && (
                <p className="about-history-modal-desc">{expandedHistoryImage.description}</p>
              )}
            </div>
          </div>
        )}
      </section>

      {/* School Leadership Section - Redesigned Light Prestige Showcase */}
      {leadershipProfiles.length > 0 && (
        <section className="about-leadership-shell" id="school-leadership">
          <div className="about-leadership-container">
            {/* Centered Section Header */}
            <div className="about-leadership-header">
              <div className="about-leadership-kicker-wrap">
                <span className="about-leadership-kicker-badge">
                  <span className="about-leadership-kicker-dot" />
                  <span>{leadSec.kicker || 'School Leadership'}</span>
                </span>
              </div>
              <h2 className="about-leadership-title">
                {leadSec.title || 'Profiles of the Principal & Key Staff'}
              </h2>
              <div className="about-leadership-title-accent" aria-hidden="true" />
              <p className="about-leadership-subtitle">
                {leadSec.subtitle || 'Meet the leaders who shape our culture, strengthen academics, and support every student journey.'}
              </p>
            </div>

            {/* Leadership Cards Grid */}
            <div className="about-leadership-grid">
              {leadershipProfiles.map((leader, idx) => {
                const initials = (() => {
                  if (!leader || typeof leader.name !== 'string') return 'PH';
                  const clean = leader.name.replace(/^(Dr\.|Mr\.|Mrs\.|Ms\.|Prof\.)\s*/i, '').trim();
                  const parts = clean.split(/\s+/).filter(Boolean);
                  if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
                  return (parts[0] ? parts[0].slice(0, 2) : 'PH').toUpperCase();
                })();

                return (
                  <article
                    className="about-leadership-card"
                    key={leader.name || idx}
                    onClick={() => setSelectedLeader(leader)}
                    tabIndex={0}
                    role="button"
                    aria-label={`View profile of ${leader.name}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setSelectedLeader(leader);
                      }
                    }}
                  >
                    {/* Top Prestige Multi-Tone Ribbon */}
                    <div className="about-leadership-card-ribbon" />

                    {/* Portrait Media Frame */}
                    <div className="about-leadership-photo-wrap">
                      {leader.photo ? (
                        <div className="about-leadership-photo-frame">
                          <img
                            src={leader.photo}
                            alt={leader.name}
                            className="about-leadership-photo-img"
                            loading="lazy"
                          />
                          <div className="about-leadership-photo-overlay" aria-hidden="true" />
                          <span className="about-leadership-view-pill" aria-hidden="true">
                            <span>View Bio</span>
                          </span>
                        </div>
                      ) : (
                        <div className="about-leadership-monogram-frame" aria-hidden="true">
                          <div className="about-leadership-monogram-glow" />
                          <div className="about-leadership-monogram-circle">
                            <span className="about-leadership-monogram-text">{initials}</span>
                          </div>
                          <span className="about-leadership-monogram-tag">Executive Council</span>
                        </div>
                      )}
                    </div>

                    {/* Card Body Information */}
                    <div className="about-leadership-body">
                      {leader.role && (
                        <span className="about-leadership-role-tag">
                          {leader.role}
                        </span>
                      )}

                      <h3 className="about-leadership-name">{leader.name}</h3>

                      <div className="about-leadership-card-divider" aria-hidden="true" />

                      {leader.bio && (
                        <p className="about-leadership-bio-text">{leader.bio}</p>
                      )}

                      <div className="about-leadership-card-footer">
                        <span className="about-leadership-more-link">
                          <span>Read Profile</span>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="5" y1="12" x2="19" y2="12" />
                            <polyline points="12 5 19 12 12 19" />
                          </svg>
                        </span>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          {/* Leader Profile Detail Modal */}
          {selectedLeader && (
            <div
              className="about-leadership-modal-backdrop"
              onClick={() => setSelectedLeader(null)}
              role="dialog"
              aria-modal="true"
              aria-label={`Profile of ${selectedLeader.name}`}
            >
              <div className="about-leadership-modal-card" onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  className="about-leadership-modal-close"
                  onClick={() => setSelectedLeader(null)}
                  aria-label="Close modal"
                >
                  &times;
                </button>
                <div className="about-leadership-modal-ribbon" />

                <div className="about-leadership-modal-grid">
                  <div className="about-leadership-modal-photo-col">
                    {selectedLeader.photo ? (
                      <img
                        src={selectedLeader.photo}
                        alt={selectedLeader.name}
                        className="about-leadership-modal-img"
                      />
                    ) : (
                      <div className="about-leadership-modal-monogram">
                        <span>
                          {(() => {
                            if (!selectedLeader || typeof selectedLeader.name !== 'string') return 'PH';
                            const clean = selectedLeader.name.replace(/^(Dr\.|Mr\.|Mrs\.|Ms\.|Prof\.)\s*/i, '').trim();
                            const parts = clean.split(/\s+/).filter(Boolean);
                            if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
                            return (parts[0] ? parts[0].slice(0, 2) : 'PH').toUpperCase();
                          })()}
                        </span>
                        <p>Permata Harapan</p>
                      </div>
                    )}
                  </div>

                  <div className="about-leadership-modal-info-col">
                    {selectedLeader.role && (
                      <span className="about-leadership-modal-role">
                        {selectedLeader.role}
                      </span>
                    )}
                    <h3 className="about-leadership-modal-name">
                      {selectedLeader.name}
                    </h3>
                    <div className="about-leadership-modal-accent" />
                    {selectedLeader.bio && (
                      <p className="about-leadership-modal-bio">
                        {selectedLeader.bio}
                      </p>
                    )}
                    <div className="about-leadership-modal-institution">
                      <span>Sekolah Permata Harapan</span>
                      <small>Commitment to Academic Excellence & Character Formation</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>
      )}
    </section>
  );
}

export default AboutUsPage;
